import {User as OidcUser} from "oidc-client-ts"

import IDataFetcher from "./IDataFetcher";
import {HttpJsonError} from "./HttpError";
import {ProfileEntityAsJsonSchema, ProfileList, ProfileQueryEmbeddedItemData} from "./models";
import {getFilenameFromContentDisposition, updateRelatedData} from "./repository-helpers";
import * as jaaropgaveData from '../json-schema/jaaropgave-documents.json';
import * as loonstrookData from '../json-schema/loonstrook-documents.json';
import * as factuurData from '../json-schema/factuur-documents.json';
import * as lijstwerkData from '../json-schema/lijstwerk-documents.json';
import * as mijnhandtekeningData from '../json-schema/mijnhandtekening-documents.json'
import {getConfig} from "../app-helpers";
import {CombinedProfileAndJsonSchema, EntityProfile, QueryResultItem, QueryResults} from "./models_v2";

const exceptionJaaropgaveSlug = 'jaaropgave-documents'
const exceptionLoonstrookSlug = 'loonstrook-documents'
const exceptionFactuurSlug = 'factuur-documents'
const exceptionLijstwerkSlug = 'lijstwerk-documents'
const exceptionMijnhandtekeningSlug = 'mijnhandtekening-documents'

export default class DataFetcher implements IDataFetcher {
    private config = getConfig()
    private baseUrl = this.config?.baseUrl
    private authority = this.config?.oidc?.authority
    private clientId = this.config?.oidc?.client_id

    public async getRootProfileData(): Promise<ProfileList> {
        const jsonData = await this.fetch(this.baseUrl+'profile');

        return jsonData.json();
    }

    public async getCombinedProfileAndJsonSchema(slug: string): Promise<CombinedProfileAndJsonSchema> {
        const [profileData, jsonSchema] = await Promise.all([
            this.getEntityProfileData(slug),
            this.getEntityProfileAsJsonSchema(slug)
        ] as [EntityProfile, ProfileEntityAsJsonSchema]);
        return {
            jsonschema: jsonSchema as ProfileEntityAsJsonSchema,
            jsonhal: profileData as EntityProfile
        };
    }

    public async getEntityProfileData(slug: string): Promise<EntityProfile> {
        const resp = await this.fetch(this.baseUrl+'profile/'+slug);
        return resp.json();
    }

    public async getEntityProfileAsJsonSchema(slug: string): Promise<ProfileEntityAsJsonSchema> {
        // FIXME. temporary solution
        // [RGFNLSLA-860] the UI-element uses JSONSchema to generate forms. To avoid having to build forms ourselves, we continue using JSONSchema in parallel, seeing as the api still provides it as a resource
        switch (slug) {
            case exceptionJaaropgaveSlug: return jaaropgaveData;
            case exceptionLoonstrookSlug: return loonstrookData;
            case exceptionFactuurSlug: return factuurData;
            case exceptionLijstwerkSlug: return lijstwerkData;
            case exceptionMijnhandtekeningSlug: return mijnhandtekeningData;
            default:
                const jsonData = await this.fetch(this.baseUrl+'profile/'+slug, {
                    headers: { Accept: 'application/schema+json' }
                });

                return jsonData.json();
        }
    }

    public async getQueryData(slug: string, query: string): Promise<QueryResults> {
        const response = await this.fetch(this.baseUrl+slug+query);
        const jsonData = await response.json()
        return jsonData;
    }

    public async getQueryDataByUrl(url: string): Promise<QueryResults> {
        const response = await this.fetch(url);
        const jsonData = await response.json()
        return jsonData;
    }

    public async getDebiteurQueryDataByUrl(url: string): Promise<ProfileQueryEmbeddedItemData[]> {
        const response = await this.fetch(url);
        const jsonData = await response.json()
        return jsonData._embedded.debiteurs;
    }

    public getAccessToken(): string {
        return this.getToken() as string
    }

    public async getQueryFactuurData(slug: string, query: string): Promise<QueryResults> {
        const factuurRootSearch: QueryResults = await this.getQueryData(slug, query)
        const items: QueryResultItem = factuurRootSearch._embedded["item"]

        const debiteursFollowupSearches: Promise<QueryResults>[] = []
        for(let i = 0; i < items.length; i++){
                debiteursFollowupSearches.push(this.getQueryDataByUrl(items[i]._links["cg:relation"].find((rel) => rel.name === "debiteur").href).then((debiteurFollowupSearch) => {
                const data = {
                    'debiteur_nr': updateRelatedData(debiteurFollowupSearch._embedded["item"], 'debiteur_nr'),
                    'debiteur_naam': updateRelatedData(debiteurFollowupSearch._embedded["item"], 'debiteur_naam'),
                } as {[k: string]: string | string[]}
                factuurRootSearch._embedded["item"][i] = { ...items[i], ...data }
            }))
        }
        await Promise.all(debiteursFollowupSearches)
   
        return factuurRootSearch;
    }

    public async getQueryJaaropgaveData(slug: string, query: string): Promise<QueryResults> {
        const jaaropgaveRootSearch: QueryResults = await this.getQueryData(slug, query)
        const items: QueryResultItem[] = jaaropgaveRootSearch._embedded["item"]

        const werknemerFollowupSearches: Promise<QueryResults>[] = []
        for(let i = 0; i < items.length; i++){
                werknemerFollowupSearches.push(this.getQueryDataByUrl(items[i]._links["cg:relation"].find((rel) => rel.name === "werknemer").href).then((werknemerFollowupSearch) => {
                const data = {
                    bsn: updateRelatedData(werknemerFollowupSearch._embedded["item"], 'bsn'),
                    'werknemer_fms_code': updateRelatedData(werknemerFollowupSearch._embedded["item"], 'werknemer_fms_code'),
                    'werknemer_naam': updateRelatedData(werknemerFollowupSearch._embedded["item"], 'werknemer_naam'),
                } as {[k: string]: string | string[]}
                jaaropgaveRootSearch._embedded["item"][i] = { ...items[i], ...data }
            }))
        }
        await Promise.all(werknemerFollowupSearches)
   
        return jaaropgaveRootSearch;
    }

    public async getQueryLijstwerkData(slug: string, query: string): Promise<QueryResults> {
        const lijstwerkRootSearch: QueryResults = await this.getQueryData(slug, query)
        const items: QueryResultItem = lijstwerkRootSearch._embedded["item"]

        const werkmaatschappijenFollowupSearches: Promise<QueryResults>[] = []
        for(let i = 0; i < items.length; i++){
            werkmaatschappijenFollowupSearches.push(this.getQueryDataByUrl(items[i]._links["cg:relation"].find((rel) => rel.name === "werkmaatschappij").href).then((werkmaatschappijFollowupSearch) => {

            const data = {
                'werkmaatschappij_id': 'werkmaatschappij_id' in werkmaatschappijFollowupSearch ? werkmaatschappijFollowupSearch['werkmaatschappij_id'] : '',
                'werkmaatschappij_titel': 'werkmaatschappij_titel' in werkmaatschappijFollowupSearch ? werkmaatschappijFollowupSearch['werkmaatschappij_titel'] : '',
                'werkmaatschappij_naam': 'werkmaatschappij_naam' in werkmaatschappijFollowupSearch ? werkmaatschappijFollowupSearch['werkmaatschappij_naam'] : '',
            } as {[k: string]: string | string[]}

                lijstwerkRootSearch._embedded["item"][i] = { ...items[i], ...data }
            }))
        }

        await Promise.all(werkmaatschappijenFollowupSearches);
   
        return lijstwerkRootSearch;
    }

    public async getContent(url: string): Promise<Blob> {
        const response = await this.fetch(url);
        return await response.blob();
    }

    public async getEntityInstanceContent(url: string): Promise<any> {
        const response = await this.fetch(url, {
            headers: {'Accept': "*/*"},
            method: 'HEAD'
        })

        const length = response?.headers.get('content-length')
        const type = response?.headers.get('content-type')
        const disposition = response?.headers.get('content-disposition')
        const name = disposition ? getFilenameFromContentDisposition(disposition) : null

     
        return {
            'content-length': length,
            'content-type': type,
            'content-name': name
        }
    }

    private getToken() : String | null {
        var oidcKey = `oidc.user:${this.authority}:${this.clientId}`;
        const oidcStorage = sessionStorage.getItem(oidcKey);
        if (!oidcStorage) {
            return null;
        }

        var oidcUser =  OidcUser.fromStorageString(oidcStorage);
        return oidcUser?.access_token;
    }

    private async fetch(url: string, init?: (RequestInit | undefined)): Promise<Response> {
        const token = this.getToken();
        let method = init?.method ?? "GET";
        if (!token) {
            throw console.log('Error')
        }

        init = {
            ...init,
            headers: {
                ...init?.headers,
                Authorization: `Bearer ${token}`
            }
        }

        const response = await fetch(url, init);
        if(!response.ok) {
            throw await HttpJsonError.tryFromResponse(method, url, response);
        }

        return response;
    }
}
